import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./SponsoredAd.css";
import {
  AD_REPORT_REASONS,
  reportAd,
  reportAdImpression,
  reportAdClick,
  reportAdVideoEvent,
} from "../../services/adsApi";

// =====================================================
// SPONSORED AD — the ONE advertisement slot used in the feed.
//
// Text, image and video ads all render inside the same container, whose
// height is fixed per breakpoint in SponsoredAd.css, so every ad type has
// exactly the same height at the same screen size. Media is fitted inside
// the slot; a text ad is centred vertically in it.
//
// IMPRESSION
//   Reported once the slot has been at least half visible for one second
//   (Advertising Terms 36). Leaving the screen before that resets the clock.
//   The server decides whether it counts, so re-renders, remounts, scrolling
//   and refreshes can never add impressions.
//
// CLICK
//   The CTA, and for text / image ads the content itself (headline,
//   description, image, caption), are sibling links to the same destination
//   sharing ONE handler — never nested, so one click is one event. The click
//   is reported from that handler — a real user interaction only — and the
//   destination opens straight away in a new tab, with no interstitial. The
//   server keeps one click per displayed ad. The video itself is never a
//   link: clicking it only plays / pauses; its CTA navigates.
//   Only an https:// destination is ever opened; without one nothing is a
//   link, nothing looks clickable and the CTA is not shown.
//
// VIDEO
//   Never plays with sound by default: muted autoplay is attempted when the
//   slot is at least half visible and paused when it scrolls away. If the
//   browser refuses autoplay, the controls let the user start it.
//   Start = playback actually began; View = VIDEO_VIEW_SECONDS actually
//   played; Completion = the video ended after most of it was actually
//   played (seeking to the end does not count).
//
// REPORT
//   The "⋯" menu of a served ad offers Report Advertisement. The reason list
//   is fixed; "Other" needs an explanation. The server validates the report
//   and ties it to the ad through the serve token. Reporting never hides or
//   removes the ad — admins review reports.
//
// Dialogs are rendered into document.body: the slot clips its content
// (overflow + backdrop-filter), and the slot's height must never change.
//
// `token` is absent on a preview (admin review, advertiser form), which is
// never tracked, never autoplays and has no report menu.
// =====================================================

const VISIBLE_RATIO = 0.5;
const VISIBLE_MS = 1000;
const VIDEO_VIEW_SECONDS = 3;
const VIDEO_COMPLETE_MIN_PLAYED = 0.9;

// Seconds of the video actually played (seeks excluded).
function playedSeconds(video) {
  let total = 0;
  const ranges = video.played;
  for (let i = 0; ranges && i < ranges.length; i += 1) total += ranges.end(i) - ranges.start(i);
  return total;
}

// The destination as a link target, or null: https only, no credentials.
// The server validated it at creation; this never opens anything else.
function safeDestination(url) {
  try {
    const u = new URL(String(url || "").trim());
    return u.protocol === "https:" && !u.username && !u.password ? u.href : null;
  } catch {
    return null;
  }
}

// A dialog over the page; Escape or the backdrop cancels.
function AdDialog({ labelledBy, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="sp-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sp-modal" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        {children}
      </div>
    </div>,
    document.body
  );
}

function ReportDialog({ token, onClose, onReported }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const needsDetails = reason === "other";
  const canSend = reason && (!needsDetails || details.trim().length >= 5) && !sending;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    setError("");
    try {
      const data = await reportAd(token, reason, needsDetails ? details.trim() : "");
      setDone(data?.message || "Thank you. Our team will review this advertisement.");
      onReported();
    } catch (err) {
      const res = err?.response?.data;
      if (res?.alreadyReported) {
        setDone(res.message);
        onReported();
      } else {
        setError(res?.message || "The report could not be sent. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <AdDialog labelledBy="sp-report-title" onClose={onClose}>
      <h2 id="sp-report-title" className="sp-modal-title">Report Advertisement</h2>
      {done ? (
        <>
          <p className="sp-modal-text" role="status">{done}</p>
          <div className="sp-modal-actions">
            <button type="button" className="sp-modal-btn sp-modal-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          <fieldset className="sp-report-reasons">
            <legend className="sp-modal-text">Why are you reporting this advertisement?</legend>
            {AD_REPORT_REASONS.map((r) => (
              <label key={r.value} className="sp-report-reason">
                <input
                  type="radio"
                  name="sp-report-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </fieldset>
          {needsDetails && (
            <label className="sp-report-details">
              <span className="sp-modal-text">Please explain the problem</span>
              <textarea
                className="sp-report-textarea"
                rows={3}
                maxLength={500}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
              />
            </label>
          )}
          {error && <p className="sp-modal-error" role="alert">{error}</p>}
          <div className="sp-modal-actions">
            <button type="button" className="sp-modal-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="sp-modal-btn sp-modal-btn-primary" disabled={!canSend}>
              {sending ? "Sending…" : "Submit Report"}
            </button>
          </div>
        </form>
      )}
    </AdDialog>
  );
}

// viewToken: an admin-posted ad's token (it has no serve token). It is used
// ONLY to report that the ad was seen, so the admin can see how many members
// it reached; everything else behaves as without a token.
function SponsoredAd({ ad, token, viewToken }) {
  const seenToken = token || viewToken;
  const slotRef = useRef(null);
  const videoRef = useRef(null);
  const isVideo = ad?.adType === "video";
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // "report" | null
  const [reported, setReported] = useState(false);

  // An open menu closes on any click outside it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (!slotRef.current?.querySelector(".sp-ad-menu")?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  useEffect(() => {
    const node = slotRef.current;
    if (!seenToken || !node || typeof IntersectionObserver === "undefined") return undefined;

    let timer = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
          if (!timer) {
            timer = setTimeout(() => {
              observer.disconnect();
              reportAdImpression(seenToken);
            }, VISIBLE_MS);
          }
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0, VISIBLE_RATIO, 1] }
    );
    observer.observe(node);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [seenToken]);

  // Muted autoplay while visible; paused while scrolled away.
  useEffect(() => {
    const node = slotRef.current;
    const video = videoRef.current;
    if (!token || !isVideo || !node || !video || typeof IntersectionObserver === "undefined") return undefined;

    let started = false;
    let pausedByScroll = false;
    const tryPlay = () => {
      try {
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {}); // blocked → user can press play
      } catch {
        // play() unsupported — the controls remain.
      }
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;
        if (visible) {
          if (!started) {
            started = true;
            video.muted = true;
            tryPlay();
          } else if (pausedByScroll) {
            pausedByScroll = false;
            tryPlay();
          }
        } else if (!video.paused) {
          pausedByScroll = true;
          video.pause();
        }
      },
      { threshold: [0, VISIBLE_RATIO, 1] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [token, isVideo]);

  // Video milestones, from what was actually played.
  useEffect(() => {
    const video = videoRef.current;
    if (!token || !isVideo || !video) return undefined;

    const onPlaying = () => reportAdVideoEvent(token, "start");
    const onTimeUpdate = () => {
      if (playedSeconds(video) >= VIDEO_VIEW_SECONDS) reportAdVideoEvent(token, "view");
    };
    const onEnded = () => {
      const duration = video.duration;
      if (Number.isFinite(duration) && duration > 0 && playedSeconds(video) >= duration * VIDEO_COMPLETE_MIN_PLAYED) {
        reportAdVideoEvent(token, "view");
        reportAdVideoEvent(token, "complete");
      }
    };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);
    return () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
    };
  }, [token, isVideo]);

  if (!ad) return null;

  const href = safeDestination(ad.destinationUrl);
  // Text and image content links too; the video's playback area never does.
  const contentLinks = Boolean(href) && !isVideo;

  // Every ad link: record the click (served ads only), then open the
  // destination directly in a new tab.
  const onAdClick = (e) => {
    e.preventDefault();
    if (token) reportAdClick(token);
    window.open(href, "_blank", "noopener,noreferrer");
  };

  // Props of a content link (headline / description / image / caption).
  const linkProps = (className, label) => ({
    className: `${className} sp-ad-link`,
    href,
    target: "_blank",
    rel: "sponsored noopener noreferrer",
    onClick: onAdClick,
    ...(label ? { "aria-label": label } : {}),
  });
  // Headline, description and brand are optional on an admin-posted ad.
  const name = ad.title || ad.description || "Advertisement";
  const contentLabel = `Sponsored: ${name}${ad.businessName ? ` — ${ad.businessName}` : ""} (opens in a new tab)`;
  // Image / video caption: the headline with the description under it (a text
  // ad already shows both in its body).
  const captionTitle = ad.adType === "text" ? "" : ad.title || "";
  const captionDesc = ad.adType === "text" ? "" : ad.description || "";
  const caption = (
    <>
      {captionTitle && <span className="sp-ad-caption-title">{captionTitle}</span>}
      {captionDesc && <span className="sp-ad-caption-desc">{captionDesc}</span>}
    </>
  );

  return (
    <div className="sp-ad-slot" ref={slotRef} data-ad-type={ad.adType}>
      <div className="sp-ad" role="complementary" aria-label="Sponsored advertisement">
        <div className="sp-ad-head">
          {ad.logoUrl && <img className="sp-ad-logo" src={ad.logoUrl} alt="" loading="lazy" />}
          <span className="sp-ad-brand">{ad.businessName}</span>
          <span className="sp-ad-label">Sponsored</span>
          {token && (
            <div className="sp-ad-menu">
              <button
                type="button"
                className="sp-ad-menu-btn"
                aria-label="Advertisement options"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              >
                ⋯
              </button>
              {menuOpen && (
                <div className="sp-ad-menu-list" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="sp-ad-menu-item"
                    disabled={reported}
                    onClick={() => {
                      setMenuOpen(false);
                      setDialog("report");
                    }}
                  >
                    {reported ? "Reported" : "Report Advertisement"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`sp-ad-body sp-ad-body-${ad.adType}`}>
          {ad.adType === "image" &&
            (contentLinks ? (
              <a {...linkProps("sp-ad-media-link", contentLabel)}>
                <img className="sp-ad-media" src={ad.imageUrl} alt={name} loading="lazy" />
              </a>
            ) : (
              <img className="sp-ad-media" src={ad.imageUrl} alt={name} loading="lazy" />
            ))}
          {ad.adType === "video" && (
            <video
              ref={videoRef}
              className="sp-ad-media sp-ad-video"
              src={ad.videoUrl}
              muted
              playsInline
              controls
              preload="metadata"
            />
          )}
          {ad.adType === "text" &&
            (contentLinks ? (
              <a {...linkProps("sp-ad-text", contentLabel)}>
                {ad.title && <h3 className="sp-ad-title">{ad.title}</h3>}
                {ad.description && <p className="sp-ad-desc">{ad.description}</p>}
              </a>
            ) : (
              <div className="sp-ad-text">
                {ad.title && <h3 className="sp-ad-title">{ad.title}</h3>}
                {ad.description && <p className="sp-ad-desc">{ad.description}</p>}
              </div>
            ))}
        </div>

        <div className="sp-ad-foot">
          {ad.adType === "image" && contentLinks && (captionTitle || captionDesc) ? (
            // Same destination as the image right above it: one keyboard stop is enough.
            <a {...linkProps("sp-ad-caption")} tabIndex={-1}>
              {caption}
            </a>
          ) : (
            <span className="sp-ad-caption">{caption}</span>
          )}
          {href && ad.ctaText && (
            <a
              className="sp-ad-cta"
              href={href}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={onAdClick}
            >
              {ad.ctaText}
            </a>
          )}
        </div>
      </div>

      {dialog === "report" && token && (
        <ReportDialog token={token} onClose={() => setDialog(null)} onReported={() => setReported(true)} />
      )}
    </div>
  );
}

export default SponsoredAd;
